import type { TFunction } from 'i18next';

/**
 * サンプル種別: UI・フォーム・フィルターでは英語コードを value に使い、
 * API/DB には従来どおり日本語ラベル（下記 LEGACY_JA）を送る。
 */
export const SAMPLE_TYPE_CODE_ORDER = [
  'pathogen_stock',
  'pathogen_working',
  'blood',
  'serum',
  'tissue',
  'cell_culture',
  'dna',
  'rna',
  'protein',
  'other',
] as const;

export type SampleTypeCode = (typeof SAMPLE_TYPE_CODE_ORDER)[number];

const LEGACY_JA: Record<SampleTypeCode, string> = {
  pathogen_stock: '病原体（ストック）',
  pathogen_working: '病原体（ワーキング）',
  blood: '血液',
  serum: '血清',
  tissue: '組織',
  cell_culture: '培養細胞',
  dna: 'DNA',
  rna: 'RNA',
  protein: 'タンパク質',
  other: 'その他',
};

const LEGACY_VALUES = new Set(Object.values(LEGACY_JA));
const CODE_SET = new Set<string>(SAMPLE_TYPE_CODE_ORDER);

/**
 * 正規コード外の保存値 → 表示用 i18n キー（フィルター value には使わない）
 * 例: ケース管理ダミーの `pathogen`
 */
const DISPLAY_ONLY_I18N: Record<string, string> = {
  pathogen: 'sample_filter.pathogen',
};

/** DB や旧 localStorage の日本語値 → フォーム用コード */
export function legacySampleTypeStringToCode(stored: string | null | undefined): string {
  if (!stored) return '';
  if (CODE_SET.has(stored)) return stored;
  const entry = (SAMPLE_TYPE_CODE_ORDER as readonly SampleTypeCode[]).find(
    (code) => LEGACY_JA[code] === stored
  );
  return entry ?? '';
}

/** フォームのコード → API/DB 用の保存値（日本語または DNA/RNA 等） */
export function sampleTypeCodeToLegacyJa(code: string | null | undefined): string | undefined {
  if (!code) return undefined;
  if (code in LEGACY_JA) return LEGACY_JA[code as SampleTypeCode];
  if (LEGACY_VALUES.has(code)) return code;
  return code;
}

function resolveSampleTypeCode(raw: string | null | undefined): SampleTypeCode | '' {
  if (raw == null || raw === '') return '';
  const code = legacySampleTypeStringToCode(raw) || (CODE_SET.has(raw) ? raw : '');
  return code && CODE_SET.has(code) ? (code as SampleTypeCode) : '';
}

/** 一覧・詳細表示: 保存値を現在ロケールのラベルに */
export function formatSampleTypeDisplay(
  raw: string | null | undefined,
  t: TFunction<'translation', undefined>
): string {
  if (raw == null || raw === '') return '-';
  const code = resolveSampleTypeCode(raw);
  if (code) {
    return t(`sample.sample_types.${code}`);
  }
  const aliasKey = DISPLAY_ONLY_I18N[raw];
  if (aliasKey) return t(aliasKey);
  return raw;
}

/**
 * 一覧の狭い「タイプ」列用。長い「病原体 · ストック」等は短ラベルにし、
 * 完全名は呼び出し側の title に載せる。
 */
export function formatSampleTypeListDisplay(
  raw: string | null | undefined,
  t: TFunction<'translation', undefined>
): string {
  const full = formatSampleTypeDisplay(raw, t);
  if (full === '-') return full;
  const code = resolveSampleTypeCode(raw);
  if (!code) return full;
  const short = t(`sample.sample_types_short.${code}`, { defaultValue: '' });
  return short || full;
}
